import React from "react";
import Layout from "./layout/Layout";
import { useTranslation } from "react-i18next";
import Logo from "../assets/dmn_logo.png";
import { assetUrl } from "../utils/assetUrl";
import { useGuestCheckoutSettings } from "./context/GuestCheckoutSettingsContext";
import { useModalOnHide } from "./ui/Modal";
import { retryWithinEmbeddedElement } from "../utils/mfeElement";
import PaymentStatusMessage from "./confirmation/PaymentStatusMessage";
import { CTA_FULL_WIDTH } from "./buttons/ctaClassNames";

interface PaymentTimeoutPageProps {
  titleKey?: string;
  messageKey?: string;
  retryAction?: string;
}

/**
 * Shown when a confirmation gave up waiting for a deposit.
 *
 * Reports nothing itself. Being rendered is not what makes a timeout worth
 * reporting, and a page cannot say why it is being shown — it has no sight of
 * how long the confirmation asked for, whether the asking was failing, or which
 * pending booking it was asking about. `usePaymentPolling` raises the single
 * event at the point it decides to give up, where all of that is known.
 */
const PaymentTimeoutPage: React.FC<PaymentTimeoutPageProps> = ({ titleKey, messageKey, retryAction }) => {
  const { t } = useTranslation();
  const { isWidget } = useGuestCheckoutSettings();
  const onModalHide = useModalOnHide();
  const title = titleKey ? t(titleKey) : t("payment_timeout_title", "Payment Taking Too Long");
  const message = messageKey
    ? t(messageKey)
    : t(
        "payment_timeout_message",
        "We are still confirming your payment. Please do not pay again as this could result in a duplicate charge. Check your email for a confirmation and if nothing arrives within 10 minutes, get in touch with the venue directly.",
      );

  if (isWidget) {
    return (
      <Layout
        main={
          <PaymentStatusMessage
            title={title}
            message={message}
            box="warning"
            action={
              retryAction ? (
                <button
                  type="button"
                  onClick={() => {
                    // The widget's confirmation modal sits on the venue's own
                    // page, where the retry route does not exist — dismiss the
                    // modal so the mounted /book journey behind it resumes.
                    // Without a modal ancestor, reload the host page stripped
                    // of Stripe's redirect params instead of navigating to a
                    // Guest Checkout route on the partner origin.
                    if (onModalHide) {
                      onModalHide();
                    } else {
                      retryWithinEmbeddedElement(retryAction, new URLSearchParams(window.location.search));
                    }
                  }}
                  className={`gcw:inline-flex gcw:items-center gcw:justify-center gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 gcw:text-lg ${CTA_FULL_WIDTH}`}
                >
                  {t("retry_payment", "Try again with a different card")}
                </button>
              ) : undefined
            }
          />
        }
        aside={undefined}
      />
    );
  }

  return (
    <Layout
      main={
        <div
          className="info-page-container"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "2rem",
            textAlign: "center",
          }}
        >
          <img
            src={assetUrl(Logo)}
            alt="DesignMyNight Logo"
            style={{
              width: "250px",
              marginBottom: "2rem",
              maxWidth: "100%",
            }}
          />
          <h1
            style={{
              fontSize: "2.5rem",
              fontWeight: 700,
              color: "#47142e",
              marginBottom: "1.5rem",
              lineHeight: 1.2,
            }}
          >
            {title}
          </h1>
          <div
            style={{
              fontSize: "1.125rem",
              color: "#333",
              lineHeight: 1.8,
              maxWidth: "650px",
              textAlign: "left",
              backgroundColor: "#f8f9fa",
              padding: "2rem",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <p style={{ marginBottom: "1rem", fontWeight: 600 }}>{message}</p>
            {retryAction && (
              <button
                type="button"
                onClick={() => {
                  const urlParams = new URLSearchParams(window.location.search);
                  window.location.href = `/${retryAction}?${urlParams.toString()}`;
                }}
                style={{
                  marginTop: "1rem",
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "#47142e",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "1rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  width: "100%",
                }}
              >
                {t("retry_payment", "Try again with a different card")}
              </button>
            )}
          </div>
        </div>
      }
      aside={undefined}
    />
  );
};

export default PaymentTimeoutPage;
